import type { ApiClientCore } from './apiClient.core.js';
import type { ApiResponse } from './apiClient.types.js';
import type {
  DeletePreflightResponse,
  LocalizedNameMap,
  TenantActivateAfterRestoreResponse,
  TenantAllowedPurposes,
  TenantDeprovisioningStatusResponse,
  TenantRestoreReadinessResponse,
  TenantSurfaceScope,
  TerminateContractResult,
} from 'pi-kiosk-shared';
import type {
  EntitlementBlockAxes,
  EntitlementBlockKey,
  EntitlementStrategyJson,
} from 'pi-kiosk-shared/tenant-entitlements';

type DevApiEntitlementPolicyRow = {
  blockKey: EntitlementBlockKey;
  runtimeMode: EntitlementBlockAxes['runtimeMode'];
  visibilityMode: EntitlementBlockAxes['visibilityMode'];
  mutationMode: EntitlementBlockAxes['mutationMode'];
  strategyJson?: EntitlementStrategyJson | null;
};

type DevApiEntitlementPolicyBody = {
  revision: number;
  allowedPurposes: TenantAllowedPurposes;
  surfaceScope: TenantSurfaceScope;
  policies: readonly DevApiEntitlementPolicyRow[];
};

type DevApiEntitlementPolicyResponse = DevApiEntitlementPolicyBody & {
  catalogVersion: number;
  pickupMirrorActive?: boolean;
};

type DevApiEntitlementCatalogResponse = {
  catalogVersion: number;
  blocks: Array<{
    blockKey: EntitlementBlockKey;
    blockClass: string;
    parentKeys: EntitlementBlockKey[];
  }>;
};

export const devMethods = {
  /**
   * List tenants for dev provisioning (GET /api/v1/dev/tenants, no tenant prefix).
   * SUPER_DEV or explicit dev:tenants:read (global grant).
   */
  async getDevTenants(this: ApiClientCore): Promise<ApiResponse<{
    tenants: Array<{
      id: number;
      name: string;
      code: string;
      status: string;
      supportedLanguages: string[];
      /** Present when list projection includes cascade fields; detail GET always emits. */
      allowedCurrencies?: string[];
      defaultCurrency?: string;
      createdAt: string;
      updatedAt: string;
    }>;
    /** Soft-degrade when findAll best-effort failed — empty list is not healthy empty. */
    degraded?: boolean;
    degradedSections?: readonly string[];
  }>> {
    return this.get('/api/v1/dev/tenants', true);
  },

  async createDevTenant(
    this: ApiClientCore,
    body: {
      name: string;
      code: string;
      supportedLanguages?: string[];
      stripeIntegrationMode?: 'DIRECT' | 'CONNECT';
      stripeConnectProductModel?: 'SAAS_PLATFORM' | 'MARKETPLACE';
      customerPaymentClaimMode?: 'DISABLED' | 'ADMIN_APPROVAL' | 'AUTO';
      defaultDonationProjectId?: number | null;
      incidentEmailAlertsEnabled?: boolean;
      incidentAdminChannelAlertsEnabled?: boolean;
      immediatePickupGraceMinutes?: number;
      paymentHandoffTtlMinutes?: number;
      incompleteCheckoutTtlDays?: number;
      modernBankAccount: {
        displayName: string;
        iban: string;
        bankCode?: string | null;
        currency?: string;
        supportedCurrencies?: string[];
        reconciliationMode?: 'APP_INITIATED_ONLY' | 'BANK_FEED_AUTHORITATIVE';
        allowedPurposes?: 'PRODUCT_ONLY' | 'DONATION_ONLY' | 'BOTH';
        isDefaultForProduct?: boolean;
        isDefaultForDonation?: boolean;
        defaultDonationProjectId?: number | null;
        defaultConstantSymbol?: string;
        customerPaymentClaimMode?: 'DISABLED' | 'ADMIN_APPROVAL' | 'AUTO' | null;
        providerSlotId?: string;
        railKind?: 'FIO' | 'STRIPE_CONNECT' | 'THEPAY' | 'GOPAY';
      };
      providerPolicySlots?: Array<{
        providerKind: string;
        providerSlotId: string;
        isEnabled: boolean;
      }>;
      platformDefaults?: {
        emailProviderSlotId: string | null;
        receiptProviderSlotId: string | null;
        smsProviderSlotId: string | null;
      };
      integrationGovernance?: {
        tenantMayConfigureEmail: boolean;
        tenantMayConfigureReceipt: boolean;
        tenantMayConfigureSms: boolean;
      };
      /** Required — backend rejects the request if missing or invalid. */
      firstSuperAdminEmail: string;
      /** Preset SUPER_ADMIN password (min 8). Omit to auto-generate a temporary password. */
      firstSuperAdminPassword?: string;
      /** Invite / credentials email language (cs|en|sk). */
      emailLocale?: 'cs' | 'en' | 'sk';
      bootstrapTax?: boolean;
      /** v8.1.4 — optional Main pickup point + on-spot kiosk link after tenant create. */
      seedDefaultPickupPoint?: boolean;
      allowedPurposes?: TenantAllowedPurposes;
      surfaceScope?: TenantSurfaceScope;
      /** DEV-only — PRODUCTION (default) or TEST entitlement readiness mode. */
      entitlementReadinessMode?: 'PRODUCTION' | 'TEST';
      /** DEV-only — commercial package plan (defaults to FREE when omitted on BE). */
      commercialPlan?: 'FREE' | 'PRO' | 'GROWTH';
      /**
       * DEV-only — Enterprise contract unlocks Feature Policy CONDITIONAL/STRATEGY customization.
       * Omit/false → FIXED to plan defaults (create-safe; matches edit Enterprise rules).
       */
      enterpriseContractActive?: boolean;
      /** ENT-PR-07 — revision 0 policy rows applied in same transaction as tenant create (C15). */
      entitlementPolicy?: {
        revision: 0;
        allowedPurposes?: TenantAllowedPurposes;
        surfaceScope?: TenantSurfaceScope;
        policies: DevApiEntitlementPolicyRow[];
      };
    }
  ): Promise<ApiResponse<{
    tenant: {
      id: number;
      name: string;
      code: string;
      supportedLanguages: string[];
      /** Tenant currency allow-list (cascade root). */
      allowedCurrencies?: string[];
      defaultCurrency?: string;
      createdAt: string;
      updatedAt: string;
    };
    /** Tenant admin login URL when first SUPER_ADMIN credentials were provisioned. */
    loginUrl?: string;
    provisioningChecklist?: {
      tenantId: number;
      policySaved: boolean;
      bankAccountId: string;
      credentialsPending: string[];
      connectOnboardingPending: boolean;
      inviteEmailPending: boolean;
      bootstrapTaxPending?: boolean;
      seedDefaultPickupPointPending?: boolean;
      entitlementSideEffectsPending?: boolean;
      auditPending?: boolean;
    };
  }>> {
    return this.post('/api/v1/dev/tenants', body, true);
  },

  async retryDevTenantInvite(
    this: ApiClientCore,
    tenantId: number,
    body: { emailLocale?: 'cs' | 'en' | 'sk' } = {}
  ): Promise<
    ApiResponse<{
      queued: boolean;
      inviteEmailPending: boolean;
      idempotent: boolean;
    }>
  > {
    return this.post(`/api/v1/dev/tenants/${tenantId}/retry-invite`, body, false);
  },

  async getDevTenantPaymentPreferences(this: ApiClientCore, tenantId: number): Promise<
    ApiResponse<{
      preferences: {
        stripeIntegrationMode: 'DIRECT' | 'CONNECT';
        stripeConnectProductModel: 'SAAS_PLATFORM' | 'MARKETPLACE';
        providerPolicyRevision?: number;
        customerPaymentClaimMode?: 'DISABLED' | 'ADMIN_APPROVAL' | 'AUTO';
        pickup?: {
          allowPreparingWhileUnpaid: boolean;
          pickupReadySmsEnabled: boolean;
          exposePickupCodeOnlyWhenPaid: boolean;
          scheduledPickupEnabled: boolean;
          scheduledPickupMinAdvanceMinutes: number;
          scheduledPickupMaxAdvanceDays: number;
          pickupWindowDurationMs: number;
          ordinaryRefundRule?: 'FULL_REFUND' | 'FULL_UNTIL_PREP_STARTED' | 'NO_AUTOMATIC_REFUND';
          preparedRefundRule?: 'FULL_REFUND' | 'FULL_UNTIL_PREP_STARTED' | 'NO_AUTOMATIC_REFUND';
          scheduledPickupSlotGranularityMinutes: number;
          autoMarkReadyAtSlotStart: boolean;
          readyLeadMinutes: number;
          maxConcurrentScheduledPickups: number;
          defaultDisplayTimeZone: string;
        };
      };
    }>
  > {
    return this.get(`/api/v1/dev/tenants/${tenantId}/payment-preferences`, false);
  },

  async updateDevTenantPaymentPreferences(
    this: ApiClientCore,
    tenantId: number,
    body: {
      stripeIntegrationMode?: 'DIRECT' | 'CONNECT';
      stripeConnectProductModel?: 'SAAS_PLATFORM' | 'MARKETPLACE';
    }
  ): Promise<ApiResponse<{ updated: boolean; providerPolicyRevision: number }>> {
    return this.put(`/api/v1/dev/tenants/${tenantId}/payment-preferences`, body, false);
  },

  /**
   * V1A §4 / G11 — platform customer-account daily cash ceiling (minor units; null = unlimited).
   * Process-memory override; restart reloads CUSTOMER_ACCOUNT_DAILY_CASH_LIMIT_MINOR.
   */
  async getDevPlatformCustomerAccountDailyCashLimit(
    this: ApiClientCore
  ): Promise<
    ApiResponse<{
      customerAccountDailyCashLimitMinor: number | null;
      persistence: 'process-memory';
      envKey: 'CUSTOMER_ACCOUNT_DAILY_CASH_LIMIT_MINOR';
      survivesRestart: false;
      unlimitedHint: 'null_or_empty';
    }>
  > {
    return this.get(
      '/api/v1/dev/platform/payment-config/customer-account-daily-cash-limit',
      false
    );
  },

  async putDevPlatformCustomerAccountDailyCashLimit(
    this: ApiClientCore,
    body: { customerAccountDailyCashLimitMinor: number | null }
  ): Promise<
    ApiResponse<{
      customerAccountDailyCashLimitMinor: number | null;
      persistence: 'process-memory';
      envKey: 'CUSTOMER_ACCOUNT_DAILY_CASH_LIMIT_MINOR';
      survivesRestart: false;
      unlimitedHint: 'null_or_empty';
    }>
  > {
    return this.put(
      '/api/v1/dev/platform/payment-config/customer-account-daily-cash-limit',
      body,
      false
    );
  },

  async getDevCustomerMembershipsByEmail(
    this: ApiClientCore,
    email: string
  ): Promise<
    ApiResponse<{
      customerId: number;
      email: string;
      memberships: Array<{
        tenantId: number;
        tenantCode: string;
        tenantName: string;
        status: string;
        activatedAt: string | null;
      }>;
    }>
  > {
    const encoded = encodeURIComponent(email);
    return this.get(`/api/v1/dev/customers/memberships?email=${encoded}`, true);
  },

  async listDevCustomers(
    this: ApiClientCore,
    params: { q?: string; limit?: number; offset?: number } = {}
  ): Promise<
    ApiResponse<{
      customers: Array<{
        id: number;
        email: string | null;
        phone: string | null;
        name: string | null;
        username: string | null;
        credentialSetupCompletedAt: string;
        lastLoginAt: string | null;
        emailVerifiedAt: string | null;
        phoneVerifiedAt: string | null;
        createdAt: string;
        membershipCount: number;
      }>;
      total: number;
      limit: number;
      offset: number;
    }>
  > {
    const search = new URLSearchParams();
    if (params.q !== undefined && params.q.trim().length > 0) {
      search.set('q', params.q.trim());
    }
    if (params.limit !== undefined) {
      search.set('limit', String(params.limit));
    }
    if (params.offset !== undefined) {
      search.set('offset', String(params.offset));
    }
    const query = search.toString();
    const path =
      query.length > 0 ? `/api/v1/dev/customers?${query}` : '/api/v1/dev/customers';
    return this.get(path, true);
  },

  async getDevProviderPolicy(
    this: ApiClientCore,
    stripeIntegrationMode: 'DIRECT' | 'CONNECT' = 'DIRECT'
  ): Promise<
    ApiResponse<{
      stripeIntegrationMode: 'DIRECT' | 'CONNECT';
      slots: Array<{
        providerKind: string;
        providerSlotId: string;
        isEnabled: true;
      }>;
      catalogAvailability?: Array<{
        providerKind: string;
        providerSlotId: string;
        envEnabled: boolean;
      }>;
      policies: Array<{
        providerKind: string;
        providerId: string;
        requiredSecretKeys: string[];
        optionalSecretKeys?: string[];
        bankAccountMode: 'forbidden' | 'optional' | 'required';
        ui?: {
          label?: string;
          helpText?: string;
          secretFieldLabels?: Record<string, string>;
        };
      }>;
      /** Platform incident email delivery readiness (no recipient exposed). */
      emailAlertsAvailable?: boolean;
      /** Platform admin-channel delivery readiness (no webhook exposed). */
      adminChannelAvailable?: boolean;
    }>
  > {
    return this.get(
      `/api/v1/dev/provider-policy?stripeIntegrationMode=${stripeIntegrationMode}`,
      true
    );
  },

  async getDevTenantProviderPolicy(this: ApiClientCore, tenantId: number): Promise<
    ApiResponse<{
      stripeIntegrationMode: 'DIRECT' | 'CONNECT';
      slots: Array<{
        providerKind: string;
        providerSlotId: string;
        isEnabled: true;
      }>;
      policies: Array<{
        providerKind: string;
        providerId: string;
        requiredSecretKeys: string[];
        optionalSecretKeys?: string[];
        bankAccountMode: 'forbidden' | 'optional' | 'required';
        ui?: {
          label?: string;
          helpText?: string;
          secretFieldLabels?: Record<string, string>;
        };
      }>;
    }>
  > {
    return this.get(`/api/v1/dev/tenants/${tenantId}/provider-policy`, false);
  },

  async putDevTenantProviderPolicy(
    this: ApiClientCore,
    tenantId: number,
    body: {
      slots: Array<{
        providerKind: string;
        providerSlotId: string;
        isEnabled: boolean;
      }>;
    }
  ): Promise<ApiResponse<{ updated: boolean }>> {
    return this.put(`/api/v1/dev/tenants/${tenantId}/provider-policy`, body, false);
  },

  async getDevTenantPlatformDefaults(this: ApiClientCore, tenantId: number): Promise<
    ApiResponse<{
      defaults: {
        tenantId: number;
        emailProviderSlotId: string | null;
        receiptProviderSlotId: string | null;
        smsProviderSlotId: string | null;
      };
    }>
  > {
    return this.get(`/api/v1/dev/tenants/${tenantId}/platform-defaults`, false);
  },

  async patchDevTenantPlatformDefaults(
    this: ApiClientCore,
    tenantId: number,
    body: {
      emailProviderSlotId?: string | null;
      receiptProviderSlotId?: string | null;
      smsProviderSlotId?: string | null;
    }
  ): Promise<
    ApiResponse<{
      defaults: {
        tenantId: number;
        emailProviderSlotId: string | null;
        receiptProviderSlotId: string | null;
        smsProviderSlotId: string | null;
      };
    }>
  > {
    return this.patch(`/api/v1/dev/tenants/${tenantId}/platform-defaults`, body, false);
  },

  async getDevTenantIntegrationGovernance(this: ApiClientCore, tenantId: number): Promise<
    ApiResponse<{
      governance: {
        tenantId: number;
        tenantMayConfigureEmail: boolean;
        tenantMayConfigureReceipt: boolean;
        tenantMayConfigureSms: boolean;
      };
    }>
  > {
    return this.get(`/api/v1/dev/tenants/${tenantId}/integration-governance`, false);
  },

  async patchDevTenantIntegrationGovernance(
    this: ApiClientCore,
    tenantId: number,
    body: {
      tenantMayConfigureEmail: boolean;
      tenantMayConfigureReceipt: boolean;
      tenantMayConfigureSms: boolean;
    }
  ): Promise<
    ApiResponse<{
      governance: {
        tenantId: number;
        tenantMayConfigureEmail: boolean;
        tenantMayConfigureReceipt: boolean;
        tenantMayConfigureSms: boolean;
      };
    }>
  > {
    return this.patch(`/api/v1/dev/tenants/${tenantId}/integration-governance`, body, false);
  },

  async upsertDevTenantBankAccountRailCredentials(
    this: ApiClientCore,
    tenantId: number,
    bankAccountId: string,
    railId: string,
    secrets: Record<string, string>
  ): Promise<ApiResponse<{ updated: boolean }>> {
    return this.put(
      `/api/v1/dev/tenants/${tenantId}/bank-accounts/${encodeURIComponent(bankAccountId)}/rails/${encodeURIComponent(railId)}/credentials`,
      { secrets },
      false
    );
  },

  async deleteDevTenantBankAccountRailCredential(
    this: ApiClientCore,
    tenantId: number,
    bankAccountId: string,
    railId: string,
    secretKey: string
  ): Promise<ApiResponse<{ deleted: boolean }>> {
    return this.delete(
      `/api/v1/dev/tenants/${tenantId}/bank-accounts/${encodeURIComponent(bankAccountId)}/rails/${encodeURIComponent(railId)}/credentials/${encodeURIComponent(secretKey)}`,
      false
    );
  },

  async upsertDevTenantBankAccountAttachmentCredentials(
    this: ApiClientCore,
    tenantId: number,
    bankAccountId: string,
    attachmentId: string,
    secrets: Record<string, string>
  ): Promise<ApiResponse<{ updated: boolean }>> {
    return this.put(
      `/api/v1/dev/tenants/${tenantId}/bank-accounts/${encodeURIComponent(bankAccountId)}/attachments/${encodeURIComponent(attachmentId)}/credentials`,
      { secrets },
      false
    );
  },

  async deleteDevTenantBankAccountAttachmentCredential(
    this: ApiClientCore,
    tenantId: number,
    bankAccountId: string,
    attachmentId: string,
    secretKey: string
  ): Promise<ApiResponse<{ deleted: boolean }>> {
    return this.delete(
      `/api/v1/dev/tenants/${tenantId}/bank-accounts/${encodeURIComponent(bankAccountId)}/attachments/${encodeURIComponent(attachmentId)}/credentials/${encodeURIComponent(secretKey)}`,
      false
    );
  },

  async createDevTenantBankAccountAttachment(
    this: ApiClientCore,
    tenantId: number,
    bankAccountId: string,
    body: { providerKind: string; providerSlotId: string; isEnabled?: boolean }
  ): Promise<
    ApiResponse<{
      attachmentId: string;
      providerKind: string;
      providerSlotId: string;
      isEnabled: boolean;
    }>
  > {
    return this.post(
      `/api/v1/dev/tenants/${tenantId}/bank-accounts/${encodeURIComponent(bankAccountId)}/attachments`,
      body,
      false
    );
  },

  async deleteDevTenantBankAccountAttachment(
    this: ApiClientCore,
    tenantId: number,
    bankAccountId: string,
    attachmentId: string
  ): Promise<ApiResponse<{ deleted: boolean }>> {
    return this.delete(
      `/api/v1/dev/tenants/${tenantId}/bank-accounts/${encodeURIComponent(bankAccountId)}/attachments/${encodeURIComponent(attachmentId)}`,
      false
    );
  },

  async getDevTenantProviderStatus(this: ApiClientCore, tenantId: number): Promise<
    ApiResponse<{
      providers: Array<{
        scope: 'rail' | 'attachment' | 'policy';
        scopeId: string;
        bankAccountId?: string;
        providerKind: string;
        providerSlotId: string;
        isEnabled: boolean;
        slotEnabled: boolean;
        hasEnabledAttachment: boolean;
        hasCredentials: boolean;
        isConfigured: boolean;
        credentialsPresent: boolean;
        configurationBlocker?: 'ENCRYPTION_UNAVAILABLE';
        verificationStatus?: 'pending' | 'verified' | 'failed';
        verifiedAt?: string | null;
        lastProbeAt?: string | null;
        lastProbeSummary?: string | null;
        probeCooldownUntil?: string | null;
      }>;
    }>
  > {
    return this.get(`/api/v1/dev/tenants/${tenantId}/provider-status`, false);
  },

  async verifyDevTenantBankAccountProviderWiring(
    this: ApiClientCore,
    tenantId: number,
    bankAccountId: string,
    providerKind: string,
    providerSlotId: string
  ): Promise<
    ApiResponse<{
      verificationStatus: 'pending' | 'verified' | 'failed';
      summary: string;
      probeCooldownUntil?: string | null;
      waitSeconds?: number;
    }>
  > {
    return this.post(
      `/api/v1/dev/tenants/${tenantId}/bank-accounts/${encodeURIComponent(bankAccountId)}/provider-wiring/${encodeURIComponent(providerKind)}/${encodeURIComponent(providerSlotId)}/verify`,
      {},
      false
    );
  },

  async acknowledgeDevCashProviderRisk(
    this: ApiClientCore,
    tenantId: number,
    slotId: string
  ): Promise<ApiResponse<{ cashRiskAckAt: string; cashRiskAckByAdminId: number }>> {
    return this.post(
      `/api/v1/dev/tenants/${tenantId}/provider-wiring/cash/${encodeURIComponent(slotId)}/risk-ack`,
      {},
      false
    );
  },

  async verifyDevCashProviderWiring(
    this: ApiClientCore,
    tenantId: number,
    slotId: string
  ): Promise<
    ApiResponse<{
      verificationStatus: 'pending' | 'verified' | 'failed';
      summary: string;
      probeCooldownUntil?: string | null;
      waitSeconds?: number;
    }>
  > {
    return this.post(
      `/api/v1/dev/tenants/${tenantId}/provider-wiring/cash/${encodeURIComponent(slotId)}/verify`,
      {},
      false
    );
  },

  async startDevBankAccountStripeConnectOnboarding(
    this: ApiClientCore,
    tenantId: number,
    bankAccountId: string,
    railId: string
  ): Promise<ApiResponse<{ url: string; stripeConnectAccountId: string; expiresAt: number }>> {
    return this.post(
      `/api/v1/dev/tenants/${tenantId}/bank-accounts/${encodeURIComponent(bankAccountId)}/rails/${encodeURIComponent(railId)}/stripe-connect/onboard`,
      {},
      false
    );
  },

  async getDevBankAccountStripeConnectOnboardingStatus(
    this: ApiClientCore,
    tenantId: number,
    bankAccountId: string,
    railId: string
  ): Promise<
    ApiResponse<{
      status: {
        stripeIntegrationMode: 'DIRECT' | 'CONNECT';
        stripeConnectProductModel: 'SAAS_PLATFORM' | 'MARKETPLACE';
        stripeConnectOnboardingStatus: string;
        stripeConnectAccountId: string | null;
        chargesEnabled: boolean | null;
        payoutsEnabled: boolean | null;
        detailsSubmitted: boolean | null;
      };
    }>
  > {
    return this.get(
      `/api/v1/dev/tenants/${tenantId}/bank-accounts/${encodeURIComponent(bankAccountId)}/rails/${encodeURIComponent(railId)}/stripe-connect/status`,
      false
    );
  },

  async listDevTenantBankAccounts(this: ApiClientCore, tenantId: number): Promise<
    ApiResponse<{
      accounts: Array<{
        id: string;
        displayName: string;
        iban: string;
        bankCode: string | null;
        currency?: string;
        reconciliationMode: string;
        allowedPurposes: string;
        customerPaymentClaimMode: string | null;
        defaultDonationProjectId: number | null;
        isDefaultForProduct?: boolean;
        isDefaultForDonation?: boolean;
        defaultConstantSymbol?: string;
        isActive: boolean;
        rails?: Array<{
          id: string;
          railKind: 'FIO' | 'STRIPE_CONNECT' | 'THEPAY' | 'GOPAY';
          providerSlotId: string;
          credentialFingerprint: string | null;
          isEnabled: boolean;
        }>;
        attachments?: Array<{
          id: string;
          providerKind: string;
          providerSlotId: string;
          credentialFingerprint: string | null;
          isEnabled: boolean;
        }>;
      }>;
    }>
  > {
    return this.get(`/api/v1/dev/tenants/${tenantId}/bank-accounts`, false);
  },

  async createDevTenantBankAccount(
    this: ApiClientCore,
    tenantId: number,
    body: {
      displayName: string;
      iban: string;
      bankCode?: string | null;
      currency?: string;
      supportedCurrencies?: string[];
      reconciliationMode?: 'APP_INITIATED_ONLY' | 'BANK_FEED_AUTHORITATIVE';
      allowedPurposes?: 'PRODUCT_ONLY' | 'DONATION_ONLY' | 'BOTH';
      isDefaultForProduct?: boolean;
      isDefaultForDonation?: boolean;
      defaultDonationProjectId?: number | null;
      defaultConstantSymbol?: string;
      customerPaymentClaimMode?: 'DISABLED' | 'ADMIN_APPROVAL' | 'AUTO' | null;
      providerSlotId: string;
      railKind?: 'FIO' | 'STRIPE_CONNECT' | 'THEPAY' | 'GOPAY';
    }
  ): Promise<ApiResponse<Record<string, unknown>>> {
    return this.post(`/api/v1/dev/tenants/${tenantId}/bank-accounts`, body, false);
  },

  async updateDevTenantBankAccount(
    this: ApiClientCore,
    tenantId: number,
    bankAccountId: string,
    body: {
      displayName?: string;
      iban?: string;
      bankCode?: string | null;
      currency?: string;
      supportedCurrencies?: string[];
      reconciliationMode?: 'APP_INITIATED_ONLY' | 'BANK_FEED_AUTHORITATIVE';
      allowedPurposes?: 'PRODUCT_ONLY' | 'DONATION_ONLY' | 'BOTH';
      isDefaultForProduct?: boolean;
      isDefaultForDonation?: boolean;
      defaultDonationProjectId?: number | null;
      defaultConstantSymbol?: string;
      customerPaymentClaimMode?: 'DISABLED' | 'ADMIN_APPROVAL' | 'AUTO' | null;
      defaultGatewayProviderSlotId?: string | null;
      isActive?: boolean;
    }
  ): Promise<ApiResponse<Record<string, unknown>>> {
    return this.patch(
      `/api/v1/dev/tenants/${tenantId}/bank-accounts/${encodeURIComponent(bankAccountId)}`,
      body,
      false
    );
  },

  async getDevEntitlementCatalog(this: ApiClientCore): Promise<ApiResponse<DevApiEntitlementCatalogResponse>> {
    return this.get('/api/v1/dev/tenant-entitlements/catalog', true);
  },

  async getDevTenantEntitlementPolicy(
    this: ApiClientCore,
    tenantId: number
  ): Promise<ApiResponse<DevApiEntitlementPolicyResponse>> {
    return this.get(`/api/v1/dev/tenants/${tenantId}/entitlement-policy`, true);
  },

  async putDevTenantEntitlementPolicy(
    this: ApiClientCore,
    tenantId: number,
    body: DevApiEntitlementPolicyBody
  ): Promise<ApiResponse<DevApiEntitlementPolicyResponse>> {
    return this.put(`/api/v1/dev/tenants/${tenantId}/entitlement-policy`, body, true);
  },

  /**
   * Soft-revoke active tenant admin capability grants that fail the commercial entitlement ceiling.
   * Body `dryRun` defaults to **true** (preview) when omitted — pass `false` to execute.
   */
  async stripDevTenantNonEntitledCapabilities(
    this: ApiClientCore,
    tenantId: number,
    body?: { readonly dryRun?: boolean },
  ): Promise<
    ApiResponse<{
      dryRun: boolean;
      tenantId: number;
      matchedCount: number;
      revokedCount: number;
      sample: Array<{
        adminId: number;
        capability: string;
        missingBlockKeys: string[];
      }>;
    }>
  > {
    return this.post(
      `/api/v1/dev/tenants/${tenantId}/strip-non-entitled-capabilities`,
      { dryRun: body?.dryRun !== false },
      true,
    );
  },

  async getDevTenant(this: ApiClientCore, tenantId: number): Promise<ApiResponse<{
    tenant: {
      id: number;
      name: string;
      code: string;
      status: 'PENDING' | 'ACTIVE' | 'DEACTIVATED' | 'DELETED';
      supportedLanguages: string[];
      /** Tenant currency allow-list + default (always on GET detail). */
      allowedCurrencies: string[];
      defaultCurrency: string;
      createdAt: string;
      updatedAt: string;
      /** G10 / T1 — tenant entitlement readiness mode (PRODUCTION|TEST). */
      entitlementReadinessMode?: 'PRODUCTION' | 'TEST';
      /**
       * Steady-state FREE|PRO|GROWTH (never UNASSIGNED). Legacy DB null may appear
       * so DEV UI hydrates FREE and Save PATCHes repair (G1). Account UI fail-closes.
       */
      commercialPlan?: 'FREE' | 'PRO' | 'GROWTH' | null;
      enterpriseContractActive?: boolean;
      enterpriseEffectiveFrom?: string | null;
      paidProStatus?: 'NONE' | 'TRIAL' | 'INTERNAL_GRANT' | 'ACTIVE' | 'CANCELING' | 'ENDED';
      paidProStartedAt?: string | null;
      paidProEndedAt?: string | null;
      paidProCancelRequestedAt?: string | null;
      paidProSource?: 'MANUAL' | null;
      paidProReconciliation?: 'RECONCILED' | 'PROVISIONAL' | null;
      payment: {
        hasFioRailApiToken: boolean;
        hasBankAccount: boolean;
        bankAccountLast4: string | null;
      };
      customerFrontendUrl: string | null;
      incidentNotifications: {
        emailEnabled: boolean;
        adminChannelEnabled: boolean;
        emailAlertsAvailable?: boolean;
        adminChannelAvailable?: boolean;
        hasExistingAdminEmail?: boolean;
        hasPendingInvite?: boolean;
      };
      customerPaymentClaimMode: 'DISABLED' | 'ADMIN_APPROVAL' | 'AUTO';
      defaultDonationProjectId: number | null;
      /** Tenant Feature-policy allowed purposes (source of truth for bank purpose UI). */
      allowedPurposes?: TenantAllowedPurposes;
      surfaceScope?: TenantSurfaceScope;
      immediatePickupGraceMinutes: number;
      paymentHandoffTtlMinutes: number;
      incompleteCheckoutTtlDays: number;
      taxSystemsBootstrapped?: boolean;
      hasDefaultPickupPoint?: boolean;
      legalName?: string | null;
      ico?: string | null;
      dic?: string | null;
      orgApprovalStatus?: string;
      orgApprovalSubmittedAt?: string | null;
      orgApprovalReviewedAt?: string | null;
      orgApprovalReviewedByAdminUserId?: number | null;
      orgApprovalRejectReason?: string | null;
      orgIdentitySource?: string | null;
      bankAccounts: Array<{
        id: string;
        displayName: string;
        iban: string;
        bankCode: string | null;
        currency?: string;
        supportedCurrencies?: string[];
        reconciliationMode: string;
        allowedPurposes: string;
        customerPaymentClaimMode: string | null;
        defaultDonationProjectId: number | null;
        isDefaultForProduct: boolean;
        isDefaultForDonation: boolean;
        defaultConstantSymbol: string;
        createdAt: string;
        isActive: boolean;
        rails?: Array<{
          id: string;
          railKind: string;
          providerSlotId: string;
          credentialFingerprint: string | null;
          isEnabled: boolean;
          terminalLocationId?: string | null;
        }>;
        attachments?: Array<{
          id: string;
          providerKind: string;
          providerSlotId: string;
          credentialFingerprint: string | null;
          isEnabled: boolean;
        }>;
      }>;
    };
  }>> {
    return this.get(`/api/v1/dev/tenants/${tenantId}`, true);
  },

  async updateDevTenant(
    this: ApiClientCore,
    tenantId: number,
    body: {
      name?: string;
      supportedLanguages?: string[];
      allowedCurrencies?: string[];
      defaultCurrency?: string;
      bootstrapTax?: boolean;
      seedDefaultPickupPoint?: boolean;
      firstSuperAdminEmail?: string;
      /** Invite email language when firstSuperAdminEmail is set. */
      emailLocale?: 'cs' | 'en' | 'sk';
      incidentEmailAlertsEnabled?: boolean;
      incidentAdminChannelAlertsEnabled?: boolean;
      customerPaymentClaimMode?: 'DISABLED' | 'ADMIN_APPROVAL' | 'AUTO';
      defaultDonationProjectId?: number | null;
      immediatePickupGraceMinutes?: number;
      paymentHandoffTtlMinutes?: number;
      incompleteCheckoutTtlDays?: number;
      /** DEV-only — PRODUCTION or TEST entitlement readiness mode. */
      entitlementReadinessMode?: 'PRODUCTION' | 'TEST';
      /** DEV-only — commercial package plan. */
      commercialPlan?: 'FREE' | 'PRO' | 'GROWTH';
      /**
       * DEV-only — Enterprise contract class (separate from Free/Pro/Growth).
       * When true, SUPER_DEV may customize Feature Policy entitlements; when false,
       * entitlements stay plan-fixed.
       */
      enterpriseContractActive?: boolean;
    }
  ): Promise<ApiResponse<{
    tenant: {
      id: number;
      name: string;
      code: string;
      status: 'PENDING' | 'ACTIVE' | 'DEACTIVATED' | 'DELETED';
      supportedLanguages: string[];
      /** Tenant currency allow-list + default (PATCH returns same shape as GET detail). */
      allowedCurrencies: string[];
      defaultCurrency: string;
      createdAt: string;
      updatedAt: string;
      payment: {
        hasFioRailApiToken: boolean;
        hasBankAccount: boolean;
        bankAccountLast4: string | null;
      };
      customerFrontendUrl: string | null;
      incidentNotifications: {
        emailEnabled: boolean;
        adminChannelEnabled: boolean;
        emailAlertsAvailable?: boolean;
        adminChannelAvailable?: boolean;
        hasExistingAdminEmail?: boolean;
        hasPendingInvite?: boolean;
      };
      customerPaymentClaimMode: 'DISABLED' | 'ADMIN_APPROVAL' | 'AUTO';
      defaultDonationProjectId: number | null;
      immediatePickupGraceMinutes: number;
      paymentHandoffTtlMinutes: number;
      incompleteCheckoutTtlDays: number;
      taxSystemsBootstrapped?: boolean;
      hasDefaultPickupPoint?: boolean;
      inviteActivationUrl?: string | null;
      bankAccounts: Array<{
        id: string;
        displayName: string;
        iban: string;
        bankCode: string | null;
        currency?: string;
        supportedCurrencies?: string[];
        reconciliationMode: string;
        allowedPurposes: string;
        customerPaymentClaimMode: string | null;
        defaultDonationProjectId: number | null;
        isDefaultForProduct: boolean;
        isDefaultForDonation: boolean;
        defaultConstantSymbol: string;
        createdAt: string;
        isActive: boolean;
        rails?: Array<{
          id: string;
          railKind: string;
          providerSlotId: string;
          credentialFingerprint: string | null;
          isEnabled: boolean;
          terminalLocationId?: string | null;
        }>;
        attachments?: Array<{
          id: string;
          providerKind: string;
          providerSlotId: string;
          credentialFingerprint: string | null;
          isEnabled: boolean;
        }>;
      }>;
    };
  }>> {
    return this.patch(`/api/v1/dev/tenants/${tenantId}`, body, true);
  },

  async getDevTenantProvisioningStatus(this: ApiClientCore, tenantId: number): Promise<ApiResponse<{
    tenantId: number;
    tenantCode: string;
    checks: Array<{
      id: string;
      label: string;
      status: 'ok' | 'warn' | 'fail';
      detail?: string;
    }>;
  }>> {
    return this.get(`/api/v1/dev/tenants/${tenantId}/provisioning-status`, true);
  },

  /** G130 — mirrors provisioning-status under deprovisioning path. */
  async getDevTenantDeprovisioningStatus(
    this: ApiClientCore,
    tenantId: number
  ): Promise<ApiResponse<TenantDeprovisioningStatusResponse>> {
    return this.get(`/api/v1/dev/tenants/${tenantId}/deprovisioning-status`, true);
  },

  /**
   * POST /api/v1/dev/tenants/:id/destructive-action-otp
   * Authz: SUPER_DEV OR platform.tenants.manage (requireSuperDevOrCanonicalPermission).
   * Issues a short-lived OTP (email default; SMS when channel requested and phone on file).
   */
  async requestDevTenantDestructiveActionOtp(
    this: ApiClientCore,
    tenantId: number,
    body: {
      action: 'deactivate' | 'delete';
      channel?: 'email' | 'sms';
      /** cs | en | sk — OTP email/SMS language. */
      locale?: 'cs' | 'en' | 'sk';
    }
  ): Promise<
    ApiResponse<{
      jti: string;
      channel: 'email' | 'sms';
      expiresAt: string;
      maskedIdentifier: string;
    }>
  > {
    return this.post(`/api/v1/dev/tenants/${tenantId}/destructive-action-otp`, body, true);
  },

  async deactivateDevTenant(
    this: ApiClientCore,
    tenantId: number,
    options?: {
      confirmCode?: string;
      otpJti?: string;
      otpCode?: string;
    }
  ): Promise<ApiResponse<{
    deactivated: boolean;
  }>> {
    const params = new URLSearchParams();
    if (options?.confirmCode) {
      params.set('confirmCode', options.confirmCode);
    }
    if (options?.otpJti) {
      params.set('otpJti', options.otpJti);
    }
    if (options?.otpCode) {
      params.set('otpCode', options.otpCode);
    }
    const query = params.toString();
    return this.post(`/api/v1/dev/tenants/${tenantId}/deactivate${query ? `?${query}` : ''}`, {}, true);
  },

  async permanentlyDeleteDevTenant(
    this: ApiClientCore,
    tenantId: number,
    options?: {
      confirmCode?: string;
      otpJti?: string;
      otpCode?: string;
      exportAcknowledged?: boolean;
      reason?: string;
    }
  ): Promise<ApiResponse<{
    mode: 'legal_closure' | 'physical_delete';
    modeCode: 'modeA_legal_closure' | 'modeB_physical_delete';
    fallbackReason?: string;
    idempotent?: boolean;
    deletedCustomersCount?: number;
    evidenceArtifact?: string;
  }>> {
    const params = new URLSearchParams();
    if (options?.confirmCode) {
      params.set('confirmCode', options.confirmCode);
    }
    if (options?.otpJti) {
      params.set('otpJti', options.otpJti);
    }
    if (options?.otpCode) {
      params.set('otpCode', options.otpCode);
    }
    if (options?.exportAcknowledged !== undefined) {
      params.set('exportAcknowledged', String(options.exportAcknowledged));
    }
    if (options?.reason) {
      params.set('reason', options.reason);
    }
    const query = params.toString();
    return this.delete(`/api/v1/dev/tenants/${tenantId}/permanent-delete${query ? `?${query}` : ''}`, true);
  },

  async reactivateDevTenant(this: ApiClientCore, tenantId: number): Promise<ApiResponse<{
    reactivated: boolean;
  }>> {
    return this.post(`/api/v1/dev/tenants/${tenantId}/reactivate`, {}, true);
  },

  /** SUPER_DEV — approve org identity pending review (ARES ≠ approve). */
  async approveDevTenantOrgApproval(
    this: ApiClientCore,
    tenantId: number
  ): Promise<
    ApiResponse<{
      legalName: string | null;
      ico: string | null;
      dic: string | null;
      registeredAddress: unknown;
      orgApprovalStatus: string;
      orgApprovalSubmittedAt: string | null;
      orgApprovalReviewedAt: string | null;
      orgApprovalReviewedByAdminUserId: number | null;
      orgApprovalRejectReason: string | null;
      orgIdentitySource: string | null;
    }>
  > {
    return this.post(`/api/v1/dev/tenants/${tenantId}/org-approval/approve`, {}, true);
  },

  /** SUPER_DEV — reject org identity pending review (reason required). */
  async rejectDevTenantOrgApproval(
    this: ApiClientCore,
    tenantId: number,
    body: { reason: string }
  ): Promise<
    ApiResponse<{
      legalName: string | null;
      ico: string | null;
      dic: string | null;
      registeredAddress: unknown;
      orgApprovalStatus: string;
      orgApprovalSubmittedAt: string | null;
      orgApprovalReviewedAt: string | null;
      orgApprovalReviewedByAdminUserId: number | null;
      orgApprovalRejectReason: string | null;
      orgIdentitySource: string | null;
    }>
  > {
    return this.post(`/api/v1/dev/tenants/${tenantId}/org-approval/reject`, body, true);
  },

  async getTenantDeletePreflight(
    this: ApiClientCore,
    tenantId: number
  ): Promise<ApiResponse<DeletePreflightResponse>> {
    return this.get(`/api/v1/admin/tenants/${tenantId}/delete-preflight`, true);
  },

  async terminateTenantContract(
    this: ApiClientCore,
    tenantId: number,
    body: {
      preferPhysicalDelete?: boolean;
      acceptLegalClosureFallback?: boolean;
      immediatePhysicalDelete?: boolean;
      schedulePhysicalPurge?: boolean;
      exportAcknowledged?: boolean;
      exportJobId?: string;
      exportContentHash?: string;
      exportOverrideReason?: string;
      confirmTenantCode?: string;
      reason?: string;
      idempotencyKey?: string;
    }
  ): Promise<ApiResponse<TerminateContractResult>> {
    return this.post(`/api/v1/admin/tenants/${tenantId}/terminate-contract`, body, true);
  },

  /**
   * FI-19 / FI-27 / G19 — platform execute Owner-requested closure (PENDING→Mode A).
   * CSRF Yes via `/closure-request/execute` marker (`shouldAttachAdminAuthCsrf`).
   */
  async executeTenantClosureRequest(
    this: ApiClientCore,
    tenantId: number,
    body: { reason?: string; exportAcknowledged?: boolean } = {}
  ): Promise<ApiResponse<TerminateContractResult>> {
    return this.post(
      `/api/v1/admin/tenants/${tenantId}/closure-request/execute`,
      body,
      true
    );
  },

  /**
   * D32 deletion-list is a filesystem artifact (`var/offboarding-runs/*-offboarding-deletion-list.json`),
   * not a REST list endpoint — no admin API client. See OFFBOARDING_DELETION_LIST_AND_RESTORE_RUNBOOK.md.
   */

  async reopenTenantContract(
    this: ApiClientCore,
    tenantId: number,
    /** FI-05: reason-only — never send reactivate (activate-after-restore only). */
    body: { reason?: string } = {}
  ): Promise<ApiResponse<{
    tenantId?: number;
    tenantCode?: string;
    reactivated?: boolean;
    lifecycleEpoch?: number;
    status?: string;
    legalClosureCleared?: boolean;
    terminalOutboxResurrected?: boolean;
    newOutboxAllowed?: boolean;
  }>> {
    return this.post(`/api/v1/admin/tenants/${tenantId}/reopen-contract`, body, true);
  },

  /**
   * FI-10 / FI-18 — Mode A restore readiness (credentials, bank rail, devices, owners).
   * GET is CSRF-safe; stubs OK until backend UC ships.
   */
  async getTenantRestoreReadiness(
    this: ApiClientCore,
    tenantId: number
  ): Promise<ApiResponse<TenantRestoreReadinessResponse>> {
    return this.get(`/api/v1/admin/tenants/${tenantId}/restore-readiness`, true);
  },

  /**
   * FI-10 / FI-18 — activate DEACTIVATED tenant after reopen + readiness PASS.
   * Route zod requires typedTenantCode (exact tenant code). 409 + same blocker shape when not ready.
   */
  async activateTenantAfterRestore(
    this: ApiClientCore,
    tenantId: number,
    body: { typedTenantCode: string; reason?: string }
  ): Promise<ApiResponse<TenantActivateAfterRestoreResponse>> {
    return this.post(`/api/v1/admin/tenants/${tenantId}/activate-after-restore`, body, true);
  },

  async schedulePhysicalPurge(
    this: ApiClientCore,
    tenantId: number,
    body: { reason?: string } = {}
  ): Promise<ApiResponse<{
    physicalPurgeStatus?: string;
    physicalPurgeScheduledAt?: string;
    physicalPurgeEarliestAt?: string;
    skipped?: boolean;
    skipReason?: string;
  }>> {
    return this.post(`/api/v1/admin/tenants/${tenantId}/schedule-physical-purge`, body, true);
  },

  async cancelPhysicalPurge(
    this: ApiClientCore,
    tenantId: number,
    body: { reason?: string } = {}
  ): Promise<ApiResponse<{
    tenantId?: number;
    tenantCode?: string;
    physicalPurgeStatus?: string;
    cancelled?: boolean;
  }>> {
    return this.post(`/api/v1/admin/tenants/${tenantId}/cancel-physical-purge`, body, true);
  },

  async runRetentionNow(
    this: ApiClientCore,
    body: { dryRun?: boolean } = {},
  ): Promise<ApiResponse<{
    runId: string;
    artifactPath: string;
    contentHash: string;
    results: Array<{
      plan: string;
      class: string;
      action: string;
      eligibleCount: number;
      executedCount: number;
      note?: string;
    }>;
    legalHoldTenantCodes: string[];
  }>> {
    return this.post('/api/v1/admin/workers/retention/run-now', body, true);
  },

  async getRetentionPolicy(this: ApiClientCore): Promise<ApiResponse<{
    version: string;
    plans: Array<{
      id: string;
      retentionClass: string;
      action: string;
      retentionPeriod?: string;
      cutoffType: string;
      tableName: string;
      delegateName?: string;
      dateColumn: string;
      tenantColumn: string;
      tenantNullable: boolean;
      note?: string;
    }>;
  }>> {
    return this.get('/api/v1/admin/workers/retention/policy', true);
  },

  async putRetentionPolicy(
    this: ApiClientCore,
    body: {
      policy: {
        version: string;
        plans: Array<Record<string, unknown>>;
      };
      ticketId?: string;
      dryRun?: boolean;
    },
  ): Promise<
    ApiResponse<{
      previousPolicyVersion: string;
      nextPolicyVersion: string;
      written: boolean;
      path: string;
      changedPlanIds: string[];
      dryRun: boolean;
    }>
  > {
    return this.put('/api/v1/admin/workers/retention/policy', body, true);
  },

  /**
   * Absolute retention windows (Wave 0 G12).
   * GET `/api/v1/admin/workers/retention/windows`
   */
  async getRetentionWindows(this: ApiClientCore): Promise<
    ApiResponse<{
      settingsVersion: number;
      contentHash?: string | null;
      effectiveAt: string | null;
      windows: {
        runtimeRetentionDays: number;
        shortLivedGovernanceRetentionDays: number;
        analyticsIngestRejectionsRetentionDays: number;
        retentionPolicyV2Windows: boolean;
        reconciliationEvidencePurgeAfterDays: number;
        governanceRetentionYears: number;
        statutoryRetentionYears: number;
        customerTenantConsentsRetentionMonths: number;
      };
      provenance?: Array<{
        key: string;
        source: 'db' | 'env';
        settingsVersion?: number;
      }>;
      cutoffs?: {
        runtime?: string;
        shortLived?: string;
        analyticsIngestRejections?: string;
        reconciliation?: string;
        governance?: string;
        statutory?: string;
        customerTenantConsents?: string;
      } | null;
      envOnly?: {
        reconciliationEvidenceRetentionDays?: number | null;
        reconciliationEvidenceArchiveAfterDays?: number | null;
        reconciliationAuditManifestRetentionDays?: number | null;
        backupRetentionWindowDays?: number | null;
        physicalPurgeGraceDays?: number | null;
        outboxClassWindows?: Array<{ className: string; retentionDays: number }>;
      } | null;
      legalSignoff?: {
        approvedByLegal?: string | null;
      } | null;
      history?: Array<{
        settingsVersion: number;
        contentHash?: string | null;
        effectiveAt: string | null;
        windows?: {
          runtimeRetentionDays?: number;
          shortLivedGovernanceRetentionDays?: number;
          analyticsIngestRejectionsRetentionDays?: number;
          retentionPolicyV2Windows?: boolean;
          reconciliationEvidencePurgeAfterDays?: number;
          governanceRetentionYears?: number;
          statutoryRetentionYears?: number;
          customerTenantConsentsRetentionMonths?: number;
        } | null;
        changedKeys?: string[];
        ticketId?: string | null;
        source?: 'db' | 'env';
      }>;
    }>
  > {
    return this.get('/api/v1/admin/workers/retention/windows', true);
  },

  /**
   * PUT Absolute windows. Non-dryRun requires `ifMatchSettingsVersion`
   * (409 SETTINGS_VERSION_CONFLICT on mismatch).
   */
  async putRetentionWindows(
    this: ApiClientCore,
    body: {
      windows: {
        runtimeRetentionDays: number;
        shortLivedGovernanceRetentionDays: number;
        analyticsIngestRejectionsRetentionDays: number;
        retentionPolicyV2Windows: boolean;
        reconciliationEvidencePurgeAfterDays: number;
        governanceRetentionYears: number;
        statutoryRetentionYears: number;
        customerTenantConsentsRetentionMonths: number;
      };
      ticketId?: string;
      dryRun?: boolean;
      ifMatchSettingsVersion?: number;
    },
  ): Promise<
    ApiResponse<{
      written: boolean;
      dryRun: boolean;
      settingsVersion: number;
      contentHash?: string | null;
      changedKeys: string[];
      before: Record<string, number | boolean | null>;
      after: Record<string, number | boolean | null>;
      previewCutoffs?: {
        runtime?: string;
        shortLived?: string;
        analyticsIngestRejections?: string;
        reconciliation?: string;
        governance?: string;
        statutory?: string;
        customerTenantConsents?: string;
      } | null;
      effectiveAt: string | null;
    }>
  > {
    return this.put('/api/v1/admin/workers/retention/windows', body, true);
  },

  async getRetentionWindowsHistory(
    this: ApiClientCore,
    limit = 20,
  ): Promise<
    ApiResponse<{
      entries: Array<{
        settingsVersion: number;
        contentHash?: string | null;
        effectiveAt: string | null;
        windows?: {
          runtimeRetentionDays?: number;
          shortLivedGovernanceRetentionDays?: number;
          analyticsIngestRejectionsRetentionDays?: number;
          retentionPolicyV2Windows?: boolean;
          reconciliationEvidencePurgeAfterDays?: number;
          governanceRetentionYears?: number;
          statutoryRetentionYears?: number;
          customerTenantConsentsRetentionMonths?: number;
        } | null;
        changedKeys?: string[];
        ticketId?: string | null;
        source?: 'db' | 'env';
      }>;
    }>
  > {
    return this.get(
      `/api/v1/admin/workers/retention/windows/history?limit=${limit}`,
      true,
    );
  },

  async getRetentionManifests(this: ApiClientCore, limit = 20): Promise<ApiResponse<{
    manifests: Array<{
      id: number;
      runId: string;
      namespace: string;
      artifactFile: string;
      manifest: Record<string, unknown>;
      latestPointer?: Record<string, unknown> | null;
      createdAt: string;
    }>;
    total?: number;
  }>> {
    return this.get(`/api/v1/admin/workers/retention/manifests?limit=${limit}`, true);
  },

  async deleteRetentionManifest(this: ApiClientCore, id: number): Promise<ApiResponse<{
    deleted: boolean;
    id: number;
  }>> {
    return this.delete(`/api/v1/admin/workers/retention/manifests/${id}`, true);
  },

  async getRetentionExecutionWorkerDiagnostics(this: ApiClientCore): Promise<ApiResponse<{
    intervalMs: number;
    enableRetentionWorker: boolean;
    runOnStartup: boolean;
    dryRun: boolean;
  }>> {
    return this.get('/api/v1/admin/workers/retention/execution/diagnostics', true);
  },

  async getOutboxRetryWorkerDiagnostics(this: ApiClientCore): Promise<ApiResponse<{
    isRunning: boolean;
    isShuttingDown: boolean;
  }>> {
    return this.get('/api/v1/admin/workers/outbox-retry/diagnostics', true);
  },

  async triggerOutboxRetryWorker(this: ApiClientCore): Promise<ApiResponse<{
    accepted: boolean;
    message: string;
  }>> {
    return this.post('/api/v1/admin/workers/outbox-retry/run-now', {}, true);
  },

  async getPruneOutboxWorkerDiagnostics(this: ApiClientCore): Promise<ApiResponse<{
    intervalMs: number;
    classes: { className: string; retentionDays: number }[];
  }>> {
    return this.get('/api/v1/admin/workers/prune-outbox/diagnostics', true);
  },

  async runPruneOutboxWorkerNow(this: ApiClientCore): Promise<ApiResponse<{
    eventsDeleted: number;
    errorsDeleted: number;
  }>> {
    return this.post('/api/v1/admin/workers/prune-outbox/run-now', {}, true);
  },

  async getReceiptReconciliationWorkerDiagnostics(this: ApiClientCore): Promise<ApiResponse<{
    intervalMs: number;
    batchSize: number;
  }>> {
    return this.get('/api/v1/admin/workers/receipt-reconciliation/diagnostics', true);
  },

  async runReceiptReconciliationWorkerNow(this: ApiClientCore): Promise<ApiResponse<{
    enqueuedCount: number;
  }>> {
    return this.post('/api/v1/admin/workers/receipt-reconciliation/run-now', {}, true);
  },

  /**
   * List platform-wide promo events (GET /api/v1/dev/promotions/platform-events).
   * SUPER_DEV or loyalty:platform-campaigns:manage; global promo module must be on.
   * Backend returns `{ success, items, total }` (flat — not nested under `data`).
   */
  async listDevPlatformPromoEvents(
    this: ApiClientCore,
    params: { page?: number; limit?: number } = {},
  ): Promise<
    ApiResponse<{
      items: Array<{
        id: string;
        name: string;
        /** Optional per-locale display-name overrides (G4). */
        nameLocales?: LocalizedNameMap | null;
        status: string;
        startsAt: string | null;
        endsAt: string | null;
        ruleJson?: unknown;
        codeCount: number;
      }>;
      total: number;
    }> & {
      items?: Array<{
        id: string;
        name: string;
        /** Optional per-locale display-name overrides (G4). */
        nameLocales?: LocalizedNameMap | null;
        status: string;
        startsAt: string | null;
        endsAt: string | null;
        ruleJson?: unknown;
        codeCount: number;
      }>;
      total?: number;
    }
  > {
    const page = params.page ?? 1;
    const limit = params.limit ?? 20;
    return this.get(
      `/api/v1/dev/promotions/platform-events?page=${encodeURIComponent(String(page))}&limit=${encodeURIComponent(String(limit))}`,
      true,
    );
  },

  /**
   * Create a platform-wide promo event (POST /api/v1/dev/promotions/platform-events).
   * Backend returns `{ success, event: { eventId, ruleVersionId } }` (flat).
   */
  async createDevPlatformPromoEvent(
    this: ApiClientCore,
    body: {
      name: string;
      /** Optional per-locale display-name overrides (G4). */
      nameLocales?: LocalizedNameMap | null;
      ruleJson: unknown;
      startsAt?: string | null;
      endsAt?: string | null;
    },
  ): Promise<
    ApiResponse<{ event: { eventId: string; ruleVersionId: string } }> & {
      event?: { eventId: string; ruleVersionId: string };
    }
  > {
    return this.post('/api/v1/dev/promotions/platform-events', body, true);
  },

  /** Update a platform-wide promo event (PUT /api/v1/dev/promotions/platform-events/:id). */
  async updateDevPlatformPromoEvent(
    this: ApiClientCore,
    eventId: string,
    body: {
      name?: string;
      /** Optional per-locale display-name overrides (G4). */
      nameLocales?: LocalizedNameMap | null;
      ruleJson?: unknown;
      startsAt?: string | null;
      endsAt?: string | null;
      status?: string;
    },
  ): Promise<ApiResponse<Record<string, never>>> {
    return this.put(
      `/api/v1/dev/promotions/platform-events/${encodeURIComponent(eventId)}`,
      body,
      true,
    );
  },

  /** Pause a platform-wide promo event (POST /api/v1/dev/promotions/platform-events/:id/pause). */
  async pauseDevPlatformPromoEvent(
    this: ApiClientCore,
    eventId: string,
  ): Promise<ApiResponse<Record<string, never>>> {
    return this.post(
      `/api/v1/dev/promotions/platform-events/${encodeURIComponent(eventId)}/pause`,
      {},
      true,
    );
  },

  /**
   * List tenant enrollments for a platform promo event
   * (GET /api/v1/dev/promotions/platform-events/:eventId/enrollments).
   * Contract aligned with W2 manage API; flat or `data`-nested responses accepted.
   */
  async listDevPlatformPromoEnrollments(
    this: ApiClientCore,
    eventId: string,
  ): Promise<
    ApiResponse<{
      items: Array<{
        enrollmentId: string;
        eventId: string;
        tenantId: number;
        fundingPolicy: {
          fundedBy: 'PLATFORM' | 'TENANT' | 'SPLIT';
          platformShareBps?: number;
        } | null;
        enrolledAt: string | null;
      }>;
      total: number;
    }> & {
      items?: Array<{
        enrollmentId: string;
        eventId: string;
        tenantId: number;
        fundingPolicy: {
          fundedBy: 'PLATFORM' | 'TENANT' | 'SPLIT';
          platformShareBps?: number;
        } | null;
        enrolledAt: string | null;
      }>;
      total?: number;
    }
  > {
    return this.get(
      `/api/v1/dev/promotions/platform-events/${encodeURIComponent(eventId)}/enrollments`,
      true,
    );
  },

  /**
   * Enroll a tenant into a platform promo event
   * (POST /api/v1/dev/promotions/platform-events/:eventId/enrollments).
   */
  async createDevPlatformPromoEnrollment(
    this: ApiClientCore,
    eventId: string,
    body: {
      tenantId: number;
      fundingPolicy?: {
        fundedBy: 'PLATFORM' | 'TENANT' | 'SPLIT';
        platformShareBps?: number;
      };
    },
  ): Promise<
    ApiResponse<{
      enrollment: {
        enrollmentId: string;
        eventId: string;
        tenantId: number;
        fundingPolicy: {
          fundedBy: 'PLATFORM' | 'TENANT' | 'SPLIT';
          platformShareBps?: number;
        } | null;
        enrolledAt: string | null;
      };
    }> & {
      enrollment?: {
        enrollmentId: string;
        eventId: string;
        tenantId: number;
        fundingPolicy: {
          fundedBy: 'PLATFORM' | 'TENANT' | 'SPLIT';
          platformShareBps?: number;
        } | null;
        enrolledAt: string | null;
      };
      enrollmentId?: string;
    }
  > {
    return this.post(
      `/api/v1/dev/promotions/platform-events/${encodeURIComponent(eventId)}/enrollments`,
      body,
      true,
    );
  },

  /**
   * Update funding policy on a platform promo enrollment
   * (PATCH /api/v1/dev/promotions/platform-enrollments/:enrollmentId).
   */
  async updateDevPlatformPromoEnrollment(
    this: ApiClientCore,
    enrollmentId: string,
    body: {
      fundingPolicy: {
        fundedBy: 'PLATFORM' | 'TENANT' | 'SPLIT';
        platformShareBps?: number;
      };
    },
  ): Promise<ApiResponse<Record<string, never>>> {
    return this.patch(
      `/api/v1/dev/promotions/platform-enrollments/${encodeURIComponent(enrollmentId)}`,
      body,
      true,
    );
  },

  /**
   * Revoke (delete) a platform promo enrollment
   * (DELETE /api/v1/dev/promotions/platform-enrollments/:enrollmentId).
   */
  async revokeDevPlatformPromoEnrollment(
    this: ApiClientCore,
    enrollmentId: string,
  ): Promise<ApiResponse<Record<string, never>>> {
    return this.delete(
      `/api/v1/dev/promotions/platform-enrollments/${encodeURIComponent(enrollmentId)}`,
      true,
    );
  },

  /**
   * Platform LegalDocument CMS — list (GET /api/v1/admin/legal-documents).
   * BAN: no revoke/unpublish client methods in V1.
   */
  async listLegalDocuments(
    this: ApiClientCore,
    query?: {
      purpose?: 'TERMS_OF_SERVICE' | 'PRIVACY_NOTICE';
      locale?: 'cs' | 'en' | 'sk';
      status?: 'DRAFT' | 'PUBLISHED' | 'SUPERSEDED';
    },
  ): Promise<
    ApiResponse<{
      items: Array<{
        id: string;
        purpose: 'TERMS_OF_SERVICE' | 'PRIVACY_NOTICE';
        locale: 'cs' | 'en' | 'sk';
        version: string;
        status: 'DRAFT' | 'PUBLISHED' | 'SUPERSEDED';
        contentType: string;
        byteSize: number;
        checksum: string;
        storageKey: string;
        requiresReconsent: boolean;
        retentionSummaryHash: string | null;
        retentionSummaryStale: boolean;
        publishedAt: string | null;
        effectiveAt: string | null;
      }>;
    }>
  > {
    const params = new URLSearchParams();
    if (query?.purpose) {
      params.set('purpose', query.purpose);
    }
    if (query?.locale) {
      params.set('locale', query.locale);
    }
    if (query?.status) {
      params.set('status', query.status);
    }
    const qs = params.toString();
    return this.get(
      `/api/v1/admin/legal-documents${qs.length > 0 ? `?${qs}` : ''}`,
      true,
    );
  },

  async getLegalDocumentHistory(
    this: ApiClientCore,
    purpose: 'TERMS_OF_SERVICE' | 'PRIVACY_NOTICE',
    locale?: 'cs' | 'en' | 'sk',
  ): Promise<
    ApiResponse<{
      entries: Array<{
        id: string;
        purpose: 'TERMS_OF_SERVICE' | 'PRIVACY_NOTICE';
        locale: 'cs' | 'en' | 'sk';
        version: string;
        status: 'DRAFT' | 'PUBLISHED' | 'SUPERSEDED';
        contentType: string;
        byteSize: number;
        checksum: string;
        requiresReconsent: boolean;
        retentionSummaryHash: string | null;
        retentionSummaryStale: boolean;
        publishedAt: string | null;
        effectiveAt: string | null;
        supersedesId: string | null;
      }>;
    }>
  > {
    const qs = locale ? `?locale=${encodeURIComponent(locale)}` : '';
    return this.get(
      `/api/v1/admin/legal-documents/${encodeURIComponent(purpose)}/history${qs}`,
      true,
    );
  },

  async uploadLegalDocumentDraft(
    this: ApiClientCore,
    formData: FormData,
  ): Promise<
    ApiResponse<{
      id: string;
      purpose: 'TERMS_OF_SERVICE' | 'PRIVACY_NOTICE';
      locale: 'cs' | 'en' | 'sk';
      version: string;
      status: 'DRAFT' | 'PUBLISHED' | 'SUPERSEDED';
      storageKey: string;
      checksum: string;
      contentType: string;
      byteSize: number;
      effectiveAt: string | null;
      publishedAt: string | null;
    }>
  > {
    return this.postFormData('/api/v1/admin/legal-documents/drafts', formData, true);
  },

  async publishLegalDocument(
    this: ApiClientCore,
    documentId: string,
    body: {
      ticketId: string;
      confirm: true;
      dpoAcknowledged: true;
      requiresReconsent?: boolean;
      dryRun?: boolean;
    },
  ): Promise<
    ApiResponse<{
      dryRun: boolean;
      previousPublishedVersion: string | null;
      retentionSummaryHash: string | null;
      bakedRetentionSummary: Readonly<Record<string, number>> | null;
      effectiveAt: string;
      publishedAt: string;
      published: {
        id: string;
        purpose: 'TERMS_OF_SERVICE' | 'PRIVACY_NOTICE';
        locale: 'cs' | 'en' | 'sk';
        version: string;
        status: 'DRAFT' | 'PUBLISHED' | 'SUPERSEDED';
        storageKey: string;
        checksum: string;
        contentType: string;
        byteSize: number;
        requiresReconsent: boolean;
        retentionSummaryHash: string | null;
        retentionSummaryStale: boolean;
        effectiveAt: string | null;
        publishedAt: string | null;
      };
      superseded: {
        id: string;
        version: string;
        status: 'DRAFT' | 'PUBLISHED' | 'SUPERSEDED';
        effectiveAt: string | null;
      } | null;
    }>
  > {
    return this.post(
      `/api/v1/admin/legal-documents/${encodeURIComponent(documentId)}/publish`,
      body,
      true,
    );
  },

  async downloadLegalDocument(
    this: ApiClientCore,
    documentId: string,
  ): Promise<{
    blob: Blob;
    contentType: string;
    checksum: string | null;
    contentDisposition: string | null;
  }> {
    return this.getBlob(
      `/api/v1/admin/legal-documents/${encodeURIComponent(documentId)}/download`,
      true,
    );
  },
};
